import type { MeetingOccurrenceInput, MeetingRoomReservationInput } from '../types';
import { addIsoDays, OFFICE_END_MINUTES, OFFICE_START_MINUTES, timeFromMinutes } from './roomCalendar';

export const MAX_SERIES_DAYS = 31;

export function isIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
}

export function minutesFromTime(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})(?::00)?$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59 || minutes % 30 !== 0) return null;
  return hours * 60 + minutes;
}

export function daysInclusive(startDate: string, endDate: string): number | null {
  if (!isIsoDate(startDate) || !isIsoDate(endDate) || endDate < startDate) return null;
  const days = Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000) + 1;
  return days >= 1 && days <= MAX_SERIES_DAYS ? days : null;
}

export function occurrenceIntervalError(occurrence: Pick<MeetingOccurrenceInput, 'date' | 'startTime' | 'endTime'>, today: string): string | null {
  if (!isIsoDate(occurrence.date) || occurrence.date < today) return 'Choose a valid meeting date.';
  const start = minutesFromTime(occurrence.startTime);
  const end = minutesFromTime(occurrence.endTime);
  if (start === null || end === null) return 'Choose times in 30-minute increments.';
  if (start < OFFICE_START_MINUTES || end > OFFICE_END_MINUTES) return 'Meeting times must be within 7:30 AM–6:00 PM.';
  if (end <= start) return 'End time must be later than start time.';
  return null;
}

export function reservationIntervalError(
  reservation: MeetingRoomReservationInput,
  occurrence: MeetingOccurrenceInput,
): string | null {
  if (!reservation.roomId) return 'Choose a room.';
  const start = minutesFromTime(reservation.startTime);
  const end = minutesFromTime(reservation.endTime);
  if (start === null || end === null) return 'Choose valid room reservation times.';
  if (end <= start) return 'Room reservation end time must be after its start time.';
  if (start < OFFICE_START_MINUTES || end > OFFICE_END_MINUTES) return 'Room reservation must be within 7:30 AM–6:00 PM.';
  return null;
}

export function generateOccurrences(input: {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  fullDay: boolean;
  previous?: MeetingOccurrenceInput[];
}): MeetingOccurrenceInput[] {
  const count = daysInclusive(input.startDate, input.endDate);
  if (!count) return [];
  const prior = new Map(input.previous?.map((occurrence) => [occurrence.date, occurrence]));
  return Array.from({ length: count }, (_, index) => {
    const date = addIsoDays(input.startDate, index);
    const defaultStart = input.fullDay || index > 0 ? timeFromMinutes(OFFICE_START_MINUTES) : input.startTime;
    const defaultEnd = input.fullDay || index < count - 1 ? timeFromMinutes(OFFICE_END_MINUTES) : input.endTime;
    const existing = prior.get(date);
    return clampReservations({
      ...existing,
      date,
      startTime: defaultStart,
      endTime: defaultEnd,
      rooms: existing?.rooms ?? [],
    });
  });
}

export function clampReservations(occurrence: MeetingOccurrenceInput): MeetingOccurrenceInput {
  // Meeting and room reservation intervals are intentionally independent in v1.9.
  // Keep existing room values intact while the caller validates them against office hours.
  return occurrence;
}

export function copyFirstDayRooms(occurrences: MeetingOccurrenceInput[]): MeetingOccurrenceInput[] {
  const first = occurrences[0];
  if (!first) return occurrences;
  const firstStart = minutesFromTime(first.startTime)!;
  const firstEnd = minutesFromTime(first.endTime)!;
  return occurrences.map((occurrence, occurrenceIndex) => {
    if (occurrenceIndex === 0) return occurrence;
    const occurrenceStart = minutesFromTime(occurrence.startTime)!;
    const occurrenceEnd = minutesFromTime(occurrence.endTime)!;
    return {
      ...occurrence,
      rooms: first.rooms.map((room, roomIndex) => {
        const sourceStart = minutesFromTime(room.startTime)!;
        const sourceEnd = minutesFromTime(room.endTime)!;
        const coversFirstDay = sourceStart === firstStart && sourceEnd === firstEnd;
        const start = coversFirstDay ? occurrenceStart : Math.max(occurrenceStart, sourceStart);
        const end = coversFirstDay ? occurrenceEnd : Math.min(occurrenceEnd, sourceEnd);
        const startTime = timeFromMinutes(start < end ? start : occurrenceStart);
        const endTime = timeFromMinutes(start < end ? end : occurrenceEnd);
        const existing = occurrence.rooms.find((candidate) => candidate.roomId === room.roomId
          && candidate.startTime === startTime && candidate.endTime === endTime);
        return {
          ...room,
          clientId: existing?.clientId ?? `${occurrence.date}-${roomIndex}-${crypto.randomUUID()}`,
          reservationId: existing?.reservationId,
          startTime,
          endTime,
          useMeetingTime: room.useMeetingTime,
        };
      }),
    };
  });
}

export const START_TIME_OPTIONS = Array.from(
  { length: (OFFICE_END_MINUTES - OFFICE_START_MINUTES) / 30 },
  (_, index) => timeFromMinutes(OFFICE_START_MINUTES + index * 30),
);

export const END_TIME_OPTIONS = Array.from(
  { length: (OFFICE_END_MINUTES - OFFICE_START_MINUTES) / 30 },
  (_, index) => timeFromMinutes(OFFICE_START_MINUTES + (index + 1) * 30),
);
